import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert } from "@/integrations/supabase/types";
import {
  bulkPeriodBounds,
  bulkRecommendationRecipientLabel,
  isBulkProgramActive,
  mergeBulkDietaries,
  resolveDeliveryAnchor,
  resolveDeliveryDate,
  type BulkCelebrationCadence,
  type BulkCelebrationProgramRow,
  type DeliveryAnchor,
} from "@/lib/bulkCelebrations";
import {
  evaluateBulkRosterAdd,
  hasBlockingRosterWarning,
  type ExistingRosterMember,
  type RosterMemberInput,
} from "@/lib/bulkRosterWarnings";

export type BulkRunRow = {
  id: string;
  company_id: string;
  period_key: string;
  period_start: string;
  period_end: string;
  delivery_date: string | null;
  status: string;
  recommendation_id: string | null;
  order_id: string | null;
};

export type BulkRunMemberRow = {
  id: string;
  run_id: string;
  celebration_id: string;
  team_member_id: string;
  source: "auto" | "manual";
  excluded: boolean;
  celebration?: {
    id: string;
    date: string;
    type: string;
    status: string;
    team_members: { id: string; name: string; dietary_requirements: string[] | null } | null;
  };
};

const PROGRAM_KEY = ["bulk-program"] as const;
const RUN_KEY = (companyId: string, periodKey: string) =>
  ["bulk-run", companyId, periodKey] as const;

export function useBulkProgram(companyId: string | null | undefined) {
  return useQuery({
    queryKey: [...PROGRAM_KEY, companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<BulkCelebrationProgramRow | null> => {
      const { data, error } = await supabase
        .from("bulk_celebration_programs")
        .select(
          "company_id, enabled_at, cadence, default_tier, budget_cents, delivery_address_id, delivery_window, delivery_anchor, occasion_types, updated_at",
        )
        .eq("company_id", companyId!)
        .maybeSingle();
      if (error) throw error;
      return (data as BulkCelebrationProgramRow | null) ?? null;
    },
  });
}

async function syncAutoMembers(
  companyId: string,
  runId: string,
  periodStart: string,
  periodEnd: string,
  occasionTypes: string[],
) {
  const { data: celebrations, error } = await supabase
    .from("celebrations")
    .select("id, team_member_id, date, type")
    .eq("company_id", companyId)
    .gte("date", periodStart)
    .lte("date", periodEnd)
    .in("type", occasionTypes.length ? occasionTypes : ["birthday"]);
  if (error) throw error;

  for (const cel of celebrations ?? []) {
    await supabase.from("bulk_celebration_run_members").upsert(
      {
        run_id: runId,
        celebration_id: cel.id,
        team_member_id: cel.team_member_id,
        source: "auto",
        excluded: false,
      },
      { onConflict: "run_id,celebration_id", ignoreDuplicates: false },
    );
  }
}

export function useBulkRun(
  companyId: string | null | undefined,
  program: BulkCelebrationProgramRow | null | undefined,
  anchorDate = new Date(),
) {
  const active = isBulkProgramActive(program ?? null);
  const period = program
    ? bulkPeriodBounds(program.cadence, anchorDate)
    : null;

  const runQuery = useQuery({
    queryKey: [...RUN_KEY(companyId ?? "", period?.period_key ?? ""), "run"],
    enabled: !!companyId && active && !!period,
    queryFn: async () => {
      const deliveryAnchor = resolveDeliveryAnchor(program!);
      const { data: existing } = await supabase
        .from("bulk_celebration_runs")
        .select("*")
        .eq("company_id", companyId!)
        .eq("period_key", period!.period_key)
        .maybeSingle();

      let run = existing as BulkRunRow | null;
      if (!run) {
        const { data: inserted, error } = await supabase
          .from("bulk_celebration_runs")
          .insert({
            company_id: companyId!,
            period_key: period!.period_key,
            period_start: period!.period_start,
            period_end: period!.period_end,
            status: "draft",
          })
          .select("*")
          .single();
        if (error) throw error;
        run = inserted as BulkRunRow;
      }

      await syncAutoMembers(
        companyId!,
        run.id,
        period!.period_start,
        period!.period_end,
        program!.occasion_types?.length
          ? program!.occasion_types!
          : ["birthday"],
      );

      const { data: members, error: memErr } = await supabase
        .from("bulk_celebration_run_members")
        .select(
          `id, run_id, celebration_id, team_member_id, source, excluded,
           celebrations(id, date, type, status, team_members(id, name, dietary_requirements))`,
        )
        .eq("run_id", run.id)
        .eq("excluded", false);
      if (memErr) throw memErr;

      const celebrationDates = (members ?? [])
        .map((m) => {
          const c = m.celebrations as { date?: string } | null;
          return c?.date?.slice(0, 10);
        })
        .filter((d): d is string => !!d);

      const { delivery_date, clamped, note } = resolveDeliveryDate({
        anchor: deliveryAnchor,
        period_start: period!.period_start,
        period_end: period!.period_end,
        celebration_dates: celebrationDates,
      });

      if (run.delivery_date !== delivery_date) {
        await supabase
          .from("bulk_celebration_runs")
          .update({ delivery_date, updated_at: new Date().toISOString() })
          .eq("id", run.id);
        run = { ...run, delivery_date };
      }

      const normalizedMembers: BulkRunMemberRow[] = (members ?? []).map((m) => {
        const raw = m as BulkRunMemberRow & { celebrations?: BulkRunMemberRow["celebration"] };
        return {
          ...raw,
          celebration: raw.celebration ?? raw.celebrations ?? undefined,
        };
      });

      return {
        run,
        period,
        deliveryAnchor,
        deliveryDate: delivery_date,
        deliveryClamped: clamped,
        deliveryNote: note,
        members: normalizedMembers,
        periodLabel: period!.label,
        headcount: (members ?? []).length,
        mergedDietaries: mergeBulkDietaries(
          (members ?? []).map((m) => {
            const c = m.celebrations as {
              team_members?: { dietary_requirements?: string[] };
            } | null;
            return {
              dietary_requirements:
                c?.team_members?.dietary_requirements ?? [],
            };
          }),
        ),
        recipientLabel: bulkRecommendationRecipientLabel(
          period!.label,
          (members ?? []).length,
        ),
      };
    },
  });

  return { ...runQuery, active, period };
}

export function useSaveBulkProgram(companyId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      enabled?: boolean;
      cadence?: BulkCelebrationCadence;
      default_tier?: "standard" | "premium" | "luxury";
      budget_cents?: number | null;
      delivery_address_id?: string | null;
      delivery_anchor?: DeliveryAnchor | null;
      occasion_types?: string[];
    }) => {
      if (!companyId) throw new Error("No company");
      const row: TablesInsert<"bulk_celebration_programs"> = {
        company_id: companyId,
        updated_at: new Date().toISOString(),
      };
      if (patch.cadence) row.cadence = patch.cadence;
      if (patch.default_tier) row.default_tier = patch.default_tier;
      if (patch.budget_cents !== undefined) row.budget_cents = patch.budget_cents;
      if (patch.delivery_address_id !== undefined) {
        row.delivery_address_id = patch.delivery_address_id;
      }
      if (patch.delivery_anchor !== undefined) {
        row.delivery_anchor = patch.delivery_anchor;
      }
      if (patch.occasion_types) row.occasion_types = patch.occasion_types;
      if (patch.enabled === true) row.enabled_at = new Date().toISOString();
      if (patch.enabled === false) row.enabled_at = null;

      const { error } = await supabase
        .from("bulk_celebration_programs")
        .upsert(row, { onConflict: "company_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROGRAM_KEY });
      void qc.invalidateQueries({ queryKey: ["bulk-run"] });
    },
  });
}

export function useBulkRosterMutations(
  companyId: string | null | undefined,
  periodKey: string | undefined,
) {
  const qc = useQueryClient();

  const invalidate = () => {
    if (companyId && periodKey) {
      void qc.invalidateQueries({ queryKey: RUN_KEY(companyId, periodKey) });
    }
  };

  const excludeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from("bulk_celebration_run_members")
        .update({ excluded: true })
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const addCelebration = useMutation({
    mutationFn: async (args: {
      runId: string;
      celebrationId: string;
      teamMemberId: string;
    }) => {
      const { error } = await supabase.from("bulk_celebration_run_members").upsert(
        {
          run_id: args.runId,
          celebration_id: args.celebrationId,
          team_member_id: args.teamMemberId,
          source: "manual",
          excluded: false,
        },
        { onConflict: "run_id,celebration_id" },
      );
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { excludeMember, addCelebration, checkRosterWarnings };
}

export function checkRosterWarnings(
  candidate: RosterMemberInput,
  existing: ExistingRosterMember[],
) {
  const warnings = evaluateBulkRosterAdd(candidate, existing);
  return { warnings, blocked: hasBlockingRosterWarning(warnings) };
}
